import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ClipboardList, History, Info, LayoutDashboard } from 'lucide-react';
import type { DataProvenance, StoreProfileDetail } from '../../types';
import { OverviewTab } from '../tabs/OverviewTab';
import { SurveysPanel } from './SurveysPanel';
import { ProvenanceBadge } from '../ProvenanceBadge';

interface RecordAreaProps {
  profile: StoreProfileDetail;
  storeId?: number;
  storeIdParam?: string;
  manageMode?: boolean;
}

interface ActivityItem {
  id: string;
  date: string;
  title: string;
  description: string;
  provenance: DataProvenance;
}

interface ProvenanceRow {
  section: string;
  provenance: DataProvenance;
  source: string;
}

/**
 * Record area: the store's source of truth — identity details, surveys,
 * activity/change history and explicit data provenance per section.
 */
export function RecordArea({ profile, storeId, storeIdParam, manageMode = false }: RecordAreaProps) {
  const [subTab, setSubTab] = useState('details');

  const activityItems = useMemo<ActivityItem[]>(() => {
    const items: ActivityItem[] = [];

    for (const survey of profile.surveys) {
      items.push({
        id: `survey-${survey.id}`,
        date: survey.surveyDate,
        title: `Survey: ${survey.title}`,
        description: survey.notes ?? `Status: ${survey.status}`,
        provenance: 'live',
      });
    }
    for (const walk of profile.walkthroughs) {
      items.push({
        id: `walk-${walk.id}`,
        date: walk.captureDate,
        title: `Walk-through: ${walk.title}`,
        description: `Captured by ${walk.capturedBy}${walk.aisleNumbers.length ? ` · aisles ${walk.aisleNumbers.join(', ')}` : ''}`,
        provenance: walk.dataSource ?? 'live',
      });
    }
    for (const image of profile.referenceImages) {
      items.push({
        id: `refimg-${image.id}`,
        date: image.captureDate ?? '',
        title: `Store image: ${image.title}`,
        description: image.description ?? `Category: ${image.category.replace(/_/g, ' ')}`,
        provenance: 'live',
      });
    }
    for (const event of profile.historyEvents) {
      items.push({
        id: event.id,
        date: event.date,
        title: event.title,
        description: event.description,
        provenance: profile.isMockData ? 'demo' : 'live',
      });
    }
    for (const activity of profile.recentActivity) {
      items.push({
        id: activity.id,
        date: activity.date,
        title: activity.title,
        description: activity.description,
        provenance: 'live',
      });
    }

    return items
      .filter((item) => item.date)
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [profile]);

  const provenanceRows = useMemo<ProvenanceRow[]>(() => {
    const rows: ProvenanceRow[] = [
      {
        section: 'Store identity, address & contacts',
        provenance: 'live',
        source: manageMode ? 'Store record in REACTOR' : 'Verified store information',
      },
      {
        section: 'Twin readiness',
        provenance: 'derived',
        source: manageMode
          ? 'Calculated from setup steps (floorplan, store areas, evidence, placement, publish state)'
          : 'Overall store tour readiness',
      },
      {
        section: 'In-store locations',
        provenance: (profile.storeInstoreLocations?.length ?? 0) > 0 ? 'live' : 'missing',
        source: manageMode
          ? 'Retailer posting locations enabled for this store'
          : 'Business locations used in campaign posting',
      },
      {
        section: 'Store areas',
        provenance: profile.aisles.length > 0 || profile.floorplanZones.length > 0 ? 'live' : 'missing',
        source: manageMode ? 'Shapes drawn on the saved floorplan' : 'Physical areas on the store map',
      },
      {
        section: 'Walk-through videos',
        provenance: profile.walkthroughs.length > 0 ? 'live' : 'missing',
        source: manageMode ? 'Uploaded store walk-through recordings' : 'Store tour videos',
      },
      {
        section: 'Store imagery',
        provenance: profile.images.length + profile.referenceImages.length > 0 ? 'live' : 'missing',
        source: manageMode ? 'Campaign proof gallery and reference photos' : 'Installation and reference photos',
      },
      {
        section: 'Media inventory',
        provenance: profile.totalMediaLocations > 0 ? 'live' : 'missing',
        source: manageMode ? 'Media capacity rows for this store' : 'Installable media fixtures',
      },
      {
        section: 'Campaigns',
        provenance: profile.campaigns.length > 0 ? 'live' : 'missing',
        source: manageMode ? 'Live campaign assignments linked to this store' : 'Campaign installations',
      },
      {
        section: 'Surveys',
        provenance: profile.surveys.length > 0 ? 'live' : 'missing',
        source: manageMode ? 'Recorded store survey visits' : 'Verified store visits',
      },
    ];
    if (profile.isMockData) {
      rows.push({
        section: 'Demo enrichments',
        provenance: 'demo',
        source: 'Curated demonstration content for this example profile',
      });
    }
    return rows;
  }, [profile, manageMode]);

  return (
    <Tabs value={subTab} onValueChange={setSubTab}>
      <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0 mb-4">
        <TabsTrigger value="details" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-900 text-gray-600">
          <LayoutDashboard className="h-4 w-4 mr-1" aria-hidden />Store details
        </TabsTrigger>
        <TabsTrigger value="surveys" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-900 text-gray-600">
          <ClipboardList className="h-4 w-4 mr-1" aria-hidden />Surveys
          {profile.surveys.length > 0 && <Badge variant="secondary" className="ml-1.5">{profile.surveys.length}</Badge>}
        </TabsTrigger>
        <TabsTrigger value="activity" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-900 text-gray-600">
          <History className="h-4 w-4 mr-1" aria-hidden />Activity & history
        </TabsTrigger>
        <TabsTrigger value="provenance" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-900 text-gray-600">
          <Info className="h-4 w-4 mr-1" aria-hidden />Data provenance
        </TabsTrigger>
      </TabsList>

      <TabsContent value="details">
        <OverviewTab profile={profile} storeId={storeId} storeIdParam={storeIdParam} />
      </TabsContent>

      <TabsContent value="surveys">
        <SurveysPanel profile={profile} storeId={storeId} storeIdParam={storeIdParam} />
      </TabsContent>

      <TabsContent value="activity">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              Activity & change history
              <ProvenanceBadge provenance="derived" />
            </CardTitle>
            <p className="text-sm text-gray-500">
              Built from live surveys, walk-throughs, store imagery and gallery evidence — newest first.
            </p>
          </CardHeader>
          <CardContent>
            {activityItems.length === 0 ? (
              <p className="text-sm text-gray-500 py-8 text-center">No recorded activity for this store yet.</p>
            ) : (
              <ol className="relative border-l-2 border-gray-200 ml-3 space-y-5 pl-6">
                {activityItems.slice(0, 40).map((item) => (
                  <li key={item.id} className="relative">
                    <span className="absolute -left-[1.85rem] w-3.5 h-3.5 rounded-full bg-white border-2 border-blue-400" aria-hidden />
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-gray-500 tabular-nums">{item.date}</span>
                      <ProvenanceBadge provenance={item.provenance} />
                    </div>
                    <p className="font-medium text-sm mt-0.5">{item.title}</p>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </li>
                ))}
              </ol>
            )}
            {activityItems.length > 40 && (
              <p className="text-xs text-gray-500 mt-4">Showing the 40 most recent events of {activityItems.length}.</p>
            )}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="provenance">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              Data provenance
              <ProvenanceBadge provenance="derived" />
            </CardTitle>
            <p className="text-sm text-gray-500">
              Where every section of this profile comes from. Live and demo data are never silently blended —
              Missing means no live source exists yet.
            </p>
          </CardHeader>
          <CardContent>
            <ul className="divide-y">
              {provenanceRows.map((row) => (
                <li key={row.section} className="py-3 flex flex-wrap items-start gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-sm font-medium">{row.section}</p>
                    <p className="text-xs text-gray-500">{row.source}</p>
                  </div>
                  <ProvenanceBadge provenance={row.provenance} />
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}
